import React from "react";
import {
  createUserWithEmailAndPassword,
  getMultiFactorResolver,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  TotpMultiFactorGenerator,
  updateProfile,
} from "firebase/auth";
import {
  getFirebaseServices,
  isAppCheckConfigured,
  isFirebaseConfigured,
} from "@core/config/firebase";
import { invokeVendorCallable } from "@core/data/callable";

const AuthContext = React.createContext(null);
const DEMO_BUSINESS_STORAGE_KEY = "venpro_demo_business.nwas.v1";

const demoUser = {
  uid: "demo-vendor-owner",
  displayName: "Vendor User",
  email: "demo@venpro.app",
  photoURL: null,
};

export function formatAuthError(error) {
  if (!error) return "An unexpected error occurred.";
  const code = error?.code || "";
  switch (code) {
    case "auth/operation-not-allowed":
      return "Email/password provider is not enabled in Firebase Authentication. Enable it in the Firebase Console under Authentication > Sign-in method.";
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Invalid email or password.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/email-already-in-use":
      return "An account with this email already exists. Try signing in instead.";
    case "auth/weak-password":
      return "Password should be at least 6 characters.";
    case "auth/user-disabled":
      return "This account has been disabled. Please contact support.";
    case "auth/too-many-requests":
      return "Access temporarily locked due to too many failed attempts. Try again later or reset your password.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again.";
    case "auth/popup-closed-by-user":
      return "Sign-in was cancelled before completion.";
    case "auth/invalid-verification-code":
      return "That authenticator code is incorrect.";
    default:
      return error instanceof Error ? error.message : "Authentication failed.";
  }
}

export function AuthProvider({ children }) {
  const demoMode = import.meta.env.DEV && import.meta.env.VITE_USE_DEMO_DATA === "true";
  const services = React.useMemo(() => getFirebaseServices(), []);
  const [user, setUser] = React.useState(demoMode ? demoUser : null);
  const [isLoading, setIsLoading] = React.useState(!demoMode && isFirebaseConfigured);
  const [organizationRevision, setOrganizationRevision] = React.useState(0);
  const [mfaResolver, setMfaResolver] = React.useState(null);
  const postMfaActionRef = React.useRef(null);

  React.useEffect(() => {
    if (demoMode || !services?.auth) {
      setIsLoading(false);
      return undefined;
    }

    return onAuthStateChanged(services.auth, (nextUser) => {
      setUser(nextUser);
      setIsLoading(false);
    });
  }, [demoMode, services]);

  const handleMfaError = React.useCallback((error, afterSignIn) => {
    if (error?.code !== "auth/multi-factor-auth-required") throw error;
    const resolver = getMultiFactorResolver(services.auth, error);
    const supportsTotp = resolver.hints.some(
      (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );
    if (!supportsTotp) throw new Error("This account requires an unsupported second factor.");
    postMfaActionRef.current = afterSignIn;
    setMfaResolver(resolver);
    return { mfaRequired: true };
  }, [services]);

  const beginGoogleSignIn = React.useCallback(async (afterSignIn = null) => {
    if (demoMode) {
      setUser(demoUser);
      if (afterSignIn) await afterSignIn(demoUser);
      return { mfaRequired: false };
    }
    if (!services?.auth) throw new Error("Firebase is not configured.");
    try {
      const credential = await signInWithPopup(services.auth, new GoogleAuthProvider());
      if (afterSignIn) await afterSignIn(credential.user);
      return { mfaRequired: false };
    } catch (error) {
      return handleMfaError(error, afterSignIn);
    }
  }, [demoMode, handleMfaError, services]);

  const signInWithPassword = React.useCallback(async (emailOrUsername, password, afterSignIn = null) => {
    const identifier = String(emailOrUsername || "").trim();
    if (!identifier) throw new Error("Please enter your email or username.");
    if (!password) throw new Error("Please enter your password.");

    if (demoMode) {
      const email = identifier.includes("@") ? identifier : `${identifier}@nwautosupreme.example.test`;
      const name = identifier.split("@")[0] || demoUser.displayName;
      const demoAccount = { ...demoUser, email, displayName: name };
      setUser(demoAccount);
      if (afterSignIn) await afterSignIn(demoAccount);
      return { mfaRequired: false };
    }

    if (!services?.auth) throw new Error("Firebase is not configured.");
    try {
      const credential = await signInWithEmailAndPassword(services.auth, identifier, password);
      if (afterSignIn) await afterSignIn(credential.user);
      return { mfaRequired: false };
    } catch (error) {
      return handleMfaError(error, afterSignIn);
    }
  }, [demoMode, handleMfaError, services]);

  const signUpWithPassword = React.useCallback(async (profile, afterSignIn = null) => {
    const name = String(profile?.name || "").trim();
    const email = String(profile?.email || "").trim();
    const password = String(profile?.password || "");
    const businessName = String(profile?.businessName || "").trim();
    const businessType = String(profile?.businessType || "").trim();

    if (!email) throw new Error("Please enter a valid work email.");
    if (!password || password.length < 6) throw new Error("Password must be at least 6 characters.");
    if (!businessName) throw new Error("Please enter your business name.");

    if (demoMode) {
      localStorage.setItem(DEMO_BUSINESS_STORAGE_KEY, JSON.stringify({
        name: businessName || "NW Auto Supreme",
        businessType: businessType || "Vendor services",
      }));
      const demoAccount = {
        ...demoUser,
        displayName: name || demoUser.displayName,
        email: email || demoUser.email,
      };
      setUser(demoAccount);
      setOrganizationRevision((value) => value + 1);
      if (afterSignIn) await afterSignIn(demoAccount);
      return { mfaRequired: false };
    }

    if (!services?.auth) throw new Error("Firebase is not configured.");

    const credential = await createUserWithEmailAndPassword(services.auth, email, password);
    if (name) {
      await updateProfile(credential.user, { displayName: name });
    }

    await invokeVendorCallable("vendorsCreateOrganization", {
      businessName,
      businessType,
      displayName: name,
    });

    setOrganizationRevision((value) => value + 1);
    if (afterSignIn) await afterSignIn(credential.user);
    return { mfaRequired: false };
  }, [demoMode, services]);

  const sendPasswordReset = React.useCallback(async (email) => {
    const targetEmail = String(email || "").trim();
    if (!targetEmail) throw new Error("Please enter your email address.");

    if (demoMode) {
      return { success: true };
    }

    if (!services?.auth) throw new Error("Firebase is not configured.");
    await sendPasswordResetEmail(services.auth, targetEmail);
    return { success: true };
  }, [demoMode, services]);

  const signIn = React.useCallback(
    (credentials) => {
      if (credentials && typeof credentials === "object" && (credentials.email || credentials.username)) {
        return signInWithPassword(credentials.email || credentials.username, credentials.password);
      }
      return beginGoogleSignIn();
    },
    [beginGoogleSignIn, signInWithPassword],
  );

  const signOut = React.useCallback(async () => {
    if (demoMode) {
      setUser(null);
      return;
    }
    postMfaActionRef.current = null;
    setMfaResolver(null);
    if (services?.auth) await firebaseSignOut(services.auth);
  }, [demoMode, services]);

  const signUp = React.useCallback(async (profile) => {
    if (profile?.password) {
      return signUpWithPassword(profile);
    }
    if (demoMode) {
      localStorage.setItem(DEMO_BUSINESS_STORAGE_KEY, JSON.stringify({
        name: String(profile?.businessName || "NW Auto Supreme").trim(),
        businessType: String(profile?.businessType || "Vendor services").trim(),
      }));
      setUser({ ...demoUser, displayName: String(profile?.name || demoUser.displayName), email: String(profile?.email || demoUser.email) });
      setOrganizationRevision((value) => value + 1);
      return;
    }
    return beginGoogleSignIn(async () => {
      await invokeVendorCallable("vendorsCreateOrganization", {
        businessName: String(profile?.businessName || "").trim(),
        businessType: String(profile?.businessType || "").trim(),
        displayName: String(profile?.name || "").trim(),
      });
      setOrganizationRevision((value) => value + 1);
    });
  }, [beginGoogleSignIn, demoMode, signUpWithPassword]);

  const completeMfaSignIn = React.useCallback(async (code) => {
    if (!mfaResolver) throw new Error("No multi-factor challenge is active.");
    const normalizedCode = String(code ?? "").replace(/\D/g, "").slice(0, 6);
    if (normalizedCode.length !== 6) throw new Error("Enter the current six-digit authenticator code.");
    const hint = mfaResolver.hints.find(
      (candidate) => candidate.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );
    if (!hint) throw new Error("Authenticator-app verification is unavailable.");
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, normalizedCode);
    const credential = await mfaResolver.resolveSignIn(assertion);
    const afterSignIn = postMfaActionRef.current;
    postMfaActionRef.current = null;
    setMfaResolver(null);
    if (afterSignIn) await afterSignIn(credential.user);
    return credential.user;
  }, [mfaResolver]);

  const cancelMfaSignIn = React.useCallback(() => {
    postMfaActionRef.current = null;
    setMfaResolver(null);
  }, []);

  const value = React.useMemo(
    () => ({
      user,
      isLoading,
      isConfigured: isFirebaseConfigured,
      isAppCheckConfigured,
      isDemoMode: demoMode,
      mfaRequired: Boolean(mfaResolver),
      organizationRevision,
      signIn,
      signInWithGoogle: beginGoogleSignIn,
      signInWithPassword,
      signUp,
      signUpWithGoogle: signUp,
      signUpWithPassword,
      sendPasswordReset,
      signOut,
      completeMfaSignIn,
      cancelMfaSignIn,
      formatAuthError,
    }),
    [
      beginGoogleSignIn,
      cancelMfaSignIn,
      completeMfaSignIn,
      demoMode,
      isLoading,
      mfaResolver,
      organizationRevision,
      sendPasswordReset,
      signIn,
      signInWithPassword,
      signOut,
      signUp,
      signUpWithPassword,
      user,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider.");
  return context;
}
